import { useState, useRef, useEffect } from 'react';
import { ChevronDown, User, Ticket, Settings, LogOut } from 'lucide-react';

interface AppHeaderProps {
  userName?: string;
  onProfileClick?: () => void;
  onMyTicketsClick?: () => void;
  onSettingsClick?: () => void;
  onLogoutClick?: () => void;
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onLogoClick?: () => void;
  className?: string;
}

export const AppHeader = ({ 
  userName = "Ana García",
  onProfileClick,
  onMyTicketsClick,
  onSettingsClick,
  onLogoutClick,
  isLoggedIn = false,
  onLoginClick,
  onLogoClick,
  className = ""
}: AppHeaderProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleMenuItemClick = (action?: () => void) => {
    setIsDropdownOpen(false);
    action?.();
  };

  return (
    <div className={`bg-white border-b border-[#E5E7EB] sticky top-0 z-40 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between">
        {/* Logo */}
        <button 
          onClick={onLogoClick}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#FF6A2A] rounded-full flex items-center justify-center">
            <span className="text-xl lg:text-2xl font-bold text-white">M</span>
          </div>
          <span className="text-lg lg:text-xl font-bold text-[#111827]">Mexmo</span>
        </button>

        {/* Conditional based on login state */}
        {isLoggedIn ? (
          /* User Dropdown */
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-[#F7F8FA] rounded-[10px] transition-colors"
            >
              <div className="w-8 h-8 lg:w-9 lg:h-9 bg-[#F7F8FA] rounded-full flex items-center justify-center">
                <User className="w-4 h-4 lg:w-5 lg:h-5 text-[#6B7280]" />
              </div>
              <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-[16px] border border-[#E5E7EB] shadow-lg overflow-hidden opacity-0 animate-[fadeIn_0.2s_ease-out_forwards]">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-[#E5E7EB] bg-[#F7F8FA]">
                  <p className="text-xs text-[#6B7280]">Buenos días</p>
                  <p className="font-semibold text-[#111827]">{userName}</p>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => handleMenuItemClick(onProfileClick)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F8FA] transition-colors text-left"
                  >
                    <User className="w-5 h-5 text-[#6B7280]" />
                    <span className="font-medium text-[#111827]">Mi perfil</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuItemClick(onMyTicketsClick)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F8FA] transition-colors text-left"
                  >
                    <Ticket className="w-5 h-5 text-[#6B7280]" />
                    <span className="font-medium text-[#111827]">Mis boletos</span>
                  </button>
                  
                  <button
                    onClick={() => handleMenuItemClick(onSettingsClick)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F7F8FA] transition-colors text-left"
                  >
                    <Settings className="w-5 h-5 text-[#6B7280]" />
                    <span className="font-medium text-[#111827]">Configuración</span>
                  </button>
                </div>

                {/* Logout */}
                <div className="border-t border-[#E5E7EB]">
                  <button
                    onClick={() => handleMenuItemClick(onLogoutClick)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FEF2F2] transition-colors text-left"
                  >
                    <LogOut className="w-5 h-5 text-[#DC2626]" />
                    <span className="font-medium text-[#DC2626]">Cerrar sesión</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Login Button */
          <button
            onClick={onLoginClick}
            className="px-5 py-2.5 bg-[#FF6A2A] text-white font-medium rounded-[14px] hover:bg-[#C2410C] transition-colors text-sm"
          >
            Iniciar sesión
          </button>
        )}
      </div>
    </div>
  );
};